import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { dueChip } from '../../lib/when';
import { Icon } from './icons';
import { Avatar } from './TodoPeople';

const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/*
 * Tickets whose server said "no to-dos here" (503, 404), remembered for the
 * page's life. Fold draws an empty panel in a different place in the tree
 * from a full one, so going empty REMOUNTS this component: without the
 * memory it would mount, ask, be told 503, go empty, remount and ask again,
 * for ever.
 */
const unavailable = new Set();

/**
 * THE TO-DOS ON A TICKET (batch 4), on the staff ticket page and the
 * contractor's.
 *
 * A ticket is the job; a to-do is one of the small things the job needs -
 * order the part, ring Rita back, bring the long ladder - and those were in
 * somebody's own list where nobody else looking at the ticket could see
 * them. This shows the ones linked to this ticket that I can see (mine, and
 * those in lists shared with me), lets them be ticked from here, and adds
 * one - for me, or for the ticket's contractor or whoever raised it, who
 * get it in their own Inbox.
 *
 * `who` are the people a to-do here could be for besides me: [{ key, name,
 * label }], decided by the page, which knows who is on the ticket.
 *
 * A server without the to-do lists answers 503, and then there is no panel
 * at all - Fold hides itself when what is inside it renders nothing.
 */
export default function TicketTodos({ ticketId, auth, todoPath = '/todos', who = [], canAdd = true, onGone = null }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [gone, setGone] = useState(() => unavailable.has(String(ticketId)));   // 503, or not a list
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [forKey, setForKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const load = useCallback(async () => {
    const away = () => { unavailable.add(String(ticketId)); setGone(true); if (onGone) onGone(); };
    if (unavailable.has(String(ticketId))) { setGone(true); if (onGone) onGone(); return; }
    try {
      const r = await axios.get(`${API_BASE}/tickets/${ticketId}/todos`, auth);
      if (!Array.isArray(r.data)) { away(); return; }
      setItems(r.data.filter((t) => t && t.id != null));
      setGone(false);
    } catch (err) {
      if (err.response?.status === 503 || err.response?.status === 404) { away(); return; }
      setItems([]);
      setProblem(err.response?.data?.error || 'Could not load the to-dos');
    }
    // `auth` is rebuilt on every render of the page around it; the ticket is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  if (gone) return null;
  // Something on screen while it loads, so the panel does not go empty (and
  // remount) on its way to having rows.
  if (items === null) return <p className="tds-quiet">Loading…</p>;

  const tick = async (t) => {
    setItems((prev) => prev.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)));
    try {
      await axios.patch(`${API_BASE}/todos/${t.id}`, { done: !t.done }, auth);
    } catch (err) {
      setProblem(err.response?.data?.error || 'That did not go through');
    }
    load();
  };

  const add = async (e) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || busy) return;
    const body = { ticket_id: ticketId, title: t };
    if (forKey) body.assignee_key = forKey;
    if (due) body.due_date = due;
    setBusy(true);
    setProblem(null);
    try {
      await axios.post(`${API_BASE}/todos/from-ticket`, body, auth);
      setTitle('');
      setDue('');
      setForKey('');
      await load();
    } catch (err) {
      setProblem(err.response?.status === 503 ? 'To-dos are not set up on this server yet.'
        : err.response?.data?.error || 'That to-do could not be added');
    } finally {
      setBusy(false);
    }
  };

  const open = items.filter((t) => !t.done).length;
  const now = new Date();
  const today = isoDay(now);
  return (
    <div className="ttd">
      <span className="ttd-count">{open}</span>
      {problem && <p className="tds-error" role="alert">{problem}</p>}
      {items.length === 0 ? (
        <p className="tds-quiet">No to-dos on this ticket yet. Add the small things the job needs - a part to order, a call to return.</p>
      ) : (
        <ul className="ttd-list" aria-label="To-dos on this ticket">
          {items.map((t) => {
            const day = t.due_date ? String(t.due_date).slice(0, 10) : null;
            const state = !day || t.done ? '' : day < today ? ' is-late' : day === today ? ' is-today' : '';
            return (
              <li key={t.id} className={`ttd-item${t.done ? ' is-done' : ''}`} data-ticket-todo={t.id}>
                <input type="checkbox" className="ttd-check" checked={Boolean(t.done)} onChange={() => tick(t)}
                       aria-label={t.done ? `Mark "${t.title}" as not done` : `Mark "${t.title}" as done`} />
                <div className="ttd-main">
                  <span className="ttd-title">{t.title}</span>
                  {(day || t.assignee_key) && (
                    <span className="ttd-meta">
                      {day && (
                        <span className={`ttd-due${state}`}>
                          <Icon name="calendar" size={12} />
                          {dueChip(day, t.due_time || null, now)}
                        </span>
                      )}
                      {t.assignee_key && (
                        <Avatar name={t.assignee_name} who={t.assignee_key} label={`Assigned to ${t.assignee_name || 'somebody'}`} />
                      )}
                    </span>
                  )}
                </div>
                <button type="button" className="tds-link" aria-label={`Open "${t.title}"`}
                        onClick={() => navigate(`${todoPath}?item=${encodeURIComponent(t.id)}`)}>Open</button>
              </li>
            );
          })}
        </ul>
      )}
      {canAdd && (
        <form className="ttd-add" onSubmit={add}>
          <label className="tds-field is-title">
            <span>Add a to-do</span>
            <input type="text" value={title} maxLength={300} aria-label="Add a to-do"
                   placeholder="Order the part, ring back…" onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="tds-field is-due">
            <span>Due</span>
            <input type="date" value={due} aria-label="To-do due" onChange={(e) => setDue(e.target.value)} />
          </label>
          <label className="tds-field is-for">
            <span>For</span>
            <select value={forKey} aria-label="For" onChange={(e) => setForKey(e.target.value)}>
              <option value="">Me</option>
              {who.filter((p) => p && p.key).map((p) => (
                <option key={p.key} value={p.key}>{p.label ? `${p.name} (${p.label})` : p.name}</option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn-primary tds-btn ttd-add-btn" disabled={busy || !title.trim()}>Add</button>
        </form>
      )}
    </div>
  );
}
